import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Users, Briefcase, Calendar, FileText, Vault, CheckSquare, ShieldCheck, Target,
  Plus, Edit2, Trash2, AlertTriangle, Download, KeyRound, Lock, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import logger from '../../utils/logger';
import DirectoryTab from './tabs/DirectoryTab';
import CommitteesTab from './tabs/CommitteesTab';
import MeetingsTab from './tabs/MeetingsTab';
import ResolutionsTab from './tabs/ResolutionsTab';
import DocumentsTab from './tabs/DocumentsTab';
import ActionItemsTab from './tabs/ActionItemsTab';
import DisclosuresTab from './tabs/DisclosuresTab';
import KPIsTab from './tabs/KPIsTab';
import AccessTab from './tabs/AccessTab';
import BoardOnboardingWizard from './BoardOnboardingWizard';

const TABS = [
  { key: 'overview',  label: 'Overview',     icon: ShieldCheck },
  { key: 'directory', label: 'Directory',    icon: Users },
  { key: 'committees',label: 'Committees',   icon: Briefcase },
  { key: 'meetings',  label: 'Meetings',     icon: Calendar },
  { key: 'resolutions',label:'Resolutions',  icon: FileText },
  { key: 'documents', label: 'Documents',    icon: Vault },
  { key: 'actions',   label: 'Action Items', icon: CheckSquare },
  { key: 'disclosures',label:'Disclosures',  icon: ShieldCheck },
  { key: 'kpis',      label: 'KPIs',         icon: Target },
  { key: 'access',    label: 'Access',       icon: KeyRound, adminOnly: true },
];

export default function BoardPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  // Access requires admin OR an explicit grant (board_access flag OR a non-empty
  // board_access_orgs list). Director role alone is no longer sufficient —
  // admin must grant access via the Access tab so revocation is enforceable.
  const hasAccess = isAdmin || !!user?.board_access || ((user?.board_access_orgs || []).length > 0);
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [orgs, setOrgs] = useState([]);          // [{key, label}, ...]
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [currentOrg, setCurrentOrg] = useState(() => {
    try { return localStorage.getItem('boardOrg') || 'programs'; } catch { return 'programs'; }
  });

  // Persist org choice
  useEffect(() => {
    try { localStorage.setItem('boardOrg', currentOrg); } catch { /* ignore */ }
  }, [currentOrg]);

  // org-aware API: transparently appends ?org=currentOrg to every /board/* call
  const orgApi = useMemo(() => {
    const wrap = (method) => (url, ...rest) => {
      let finalUrl = url;
      if (typeof url === 'string' && url.includes('/board/')) {
        const sep = url.includes('?') ? '&' : '?';
        finalUrl = `${url}${sep}org=${encodeURIComponent(currentOrg)}`;
      }
      return api[method](finalUrl, ...rest);
    };
    return {
      get: wrap('get'),
      post: wrap('post'),
      put: wrap('put'),
      delete: wrap('delete'),
      patch: wrap('patch'),
    };
  }, [api, currentOrg]);

  // Fetch which orgs this user can see
  useEffect(() => {
    if (!hasAccess) return;
    (async () => {
      try {
        const { data } = await api.get('/board/orgs');
        setOrgs(data.orgs || []);
        setShowSwitcher(!!data.switcher);
        // If the user can't see currentOrg (e.g. director pinned elsewhere), snap to their default
        const allowed = (data.orgs || []).map(o => o.key);
        if (!allowed.includes(currentOrg)) setCurrentOrg(data.default);
      } catch (e) { logger.error(e); }
    })();
  }, [api, hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshSummary = useCallback(async () => {
    if (!hasAccess) return;
    try { const { data } = await orgApi.get('/board/summary'); setSummary(data); } catch (e) { logger.error(e); }
  }, [orgApi, hasAccess]);

  const fetchMembers = useCallback(async () => {
    if (!hasAccess) return;
    try { const { data } = await orgApi.get('/board/members'); setMembers(data); } catch (e) { logger.error(e); }
  }, [orgApi, hasAccess]);

  useEffect(() => { refreshSummary(); fetchMembers(); }, [refreshSummary, fetchMembers]);

  if (!hasAccess) {
    return (
      <div className="p-8 max-w-md mx-auto">
        <Card className="border-[#DED8EB] bg-white">
          <CardContent className="p-6 text-center space-y-3">
            <Lock className="h-10 w-10 mx-auto text-[#8E85A0]" />
            <h2 className="text-lg font-bold text-[#2D1B4E]">Board Portal</h2>
            <p className="text-sm text-[#5A4D6E]">Access to the Board Portal is restricted. Please contact an administrator if you need access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="board-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2D1B4E]">Board Portal</h1>
          <p className="text-sm text-[#5A4D6E]">
            {(orgs.find(o => o.key === currentOrg)?.label) || 'Board of Directors workspace'} · {summary?.today || ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showSwitcher && orgs.length > 1 && (
            <div className="flex items-center gap-1 bg-white border border-[#DED8EB] rounded-lg p-1" data-testid="org-switcher">
              <Building2 className="h-3.5 w-3.5 text-[#6B3FA0] ml-1.5" />
              {orgs.map(o => (
                <button
                  key={o.key}
                  onClick={() => setCurrentOrg(o.key)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    currentOrg === o.key ? 'bg-[#6B3FA0] text-white shadow-sm' : 'text-[#5A4D6E] hover:bg-[#F0EDF6]'
                  }`}
                  data-testid={`org-switch-${o.key}`}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
          <Badge className="bg-[#EDE7F6] text-[#6B3FA0] border border-[#9B7ED8]">
            {isAdmin ? 'Administrator' : 'Director'}
          </Badge>
        </div>
      </div>

      <Tabs key={currentOrg} value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-[#F0EDF6] p-1 mb-4 w-full">
          {TABS.filter(t => !t.adminOnly || isAdmin).map(t => (
            <TabsTrigger key={t.key} value={t.key}
              className="data-[state=active]:bg-white shrink-0 gap-1.5 text-xs"
              data-testid={`board-tab-${t.key}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"><OverviewTab summary={summary} onRefresh={refreshSummary} setTab={setTab} /></TabsContent>
        <TabsContent value="directory"><DirectoryTab api={orgApi} isAdmin={isAdmin} members={members} onChange={() => { fetchMembers(); refreshSummary(); }} /></TabsContent>
        <TabsContent value="committees"><CommitteesTab api={orgApi} isAdmin={isAdmin} members={members} onChange={refreshSummary} /></TabsContent>
        <TabsContent value="meetings"><MeetingsTab api={orgApi} isAdmin={isAdmin} members={members} onChange={refreshSummary} /></TabsContent>
        <TabsContent value="resolutions"><ResolutionsTab api={orgApi} isAdmin={isAdmin} members={members} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab api={orgApi} isAdmin={isAdmin} user={user} onChange={refreshSummary} /></TabsContent>
        <TabsContent value="actions"><ActionItemsTab api={orgApi} isAdmin={isAdmin} members={members} onChange={refreshSummary} /></TabsContent>
        <TabsContent value="disclosures"><DisclosuresTab api={orgApi} isAdmin={isAdmin} members={members} user={user} onChange={refreshSummary} /></TabsContent>
        <TabsContent value="kpis"><KPIsTab api={orgApi} isAdmin={isAdmin} members={members} /></TabsContent>
        {isAdmin && <TabsContent value="access"><AccessTab api={api} /></TabsContent>}
      </Tabs>
      <BoardOnboardingWizard />
    </div>
  );
}

function OverviewTab({ summary, setTab }) {
  if (!summary) return <div className="text-center py-12 text-[#8E85A0]">Loading…</div>;
  const cards = [
    { label: 'Active Directors',    value: summary.members,              accent: '#6B3FA0', target: 'directory' },
    { label: 'Committees',           value: summary.committees,            accent: '#43A047', target: 'committees' },
    { label: 'Upcoming Meetings',    value: summary.upcoming_meetings,     accent: '#3B82F6', target: 'meetings' },
    { label: 'Open Action Items',    value: summary.open_action_items,     accent: '#FFB74D', target: 'actions' },
    { label: 'Overdue Action Items', value: summary.overdue_action_items,  accent: summary.overdue_action_items > 0 ? '#D9534F' : '#8E85A0', target: 'actions', critical: summary.overdue_action_items > 0 },
    { label: 'Documents on File',    value: summary.documents,             accent: '#6B3FA0', target: 'documents' },
    { label: `Disclosures Missing (${summary.year})`, value: summary.missing_disclosures, accent: summary.missing_disclosures > 0 ? '#D9534F' : '#43A047', target: 'disclosures', critical: summary.missing_disclosures > 0 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="board-overview">
      {cards.map(c => (
        <Card key={c.label} className={`bg-white cursor-pointer hover:-translate-y-px transition-transform ${c.critical ? 'border-[#D9534F]' : 'border-[#DED8EB]'}`}
          onClick={() => setTab(c.target)} data-testid={`board-stat-${c.target}`}>
          <CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#5A4D6E]">{c.label}</p>
            <p className="text-3xl font-bold font-mono mt-1" style={{ color: c.accent }}>{c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
